import { useState, useEffect, useCallback, useRef } from 'react';
import api from "../api/client";

// Custom hook for managing candidates
export const useCandidates = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchCandidates = async (page, pageSize, searchQuery = '', showArchived = false, jdTag = null, companyId = null, inviteStatus = null, groupId = null) => {
    setLoading(true);
    try {
      let url = `admin/candidates/?page=${page}&page_size=${pageSize}&ordering=-updated_at,-date_joined`;
      if (searchQuery?.trim()) {
        // Use unified search to match email, firstName, lastName, and phone (mobile)
        url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      }
      if (showArchived) {
        url += `&show_archived=true`;
      }
      if (jdTag != null && jdTag !== '') {
        url += `&jd_tag=${encodeURIComponent(String(jdTag))}`;
      }
      if (companyId != null && companyId !== '') {
        url += `&company_id=${encodeURIComponent(String(companyId))}`;
      }
      if (inviteStatus != null && inviteStatus !== '') {
        url += `&invite_status=${encodeURIComponent(String(inviteStatus))}`;
      }
      if (groupId != null && groupId !== '') {
        url += `&group_id=${encodeURIComponent(String(groupId))}`;
      }

      const res = await api.get(url, {
        headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` },
      });

      const candidatesWithUrls = res?.data?.results?.map(candidate => ({
        ...candidate,
        resume_url: candidate.resume_url?.replace(window.location.origin, process.env.REACT_APP_FRONTEND_URL || 'http://localhost:8000')
      })) || [];

      // const updatedCandidates = await Promise.all(
      //   candidatesWithUrls.map(async (candidate) => {
      //     try {
      //       const sessionRes = await api.get(`sessions/?candidate=${candidate.id}&status=completed&ordering=-ended_at`, {
      //         headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` },
      //       });
      //       return { ...candidate, latestSessionId: sessionRes.data.results?.[0]?.id || null };
      //     } catch {
      //       return { ...candidate, latestSessionId: null };
      //     }
      //   })
      // );

      setCandidates(candidatesWithUrls);
      setTotalCandidates(res?.data?.pagination?.total_count || 0);
      setTotalPages(res?.data?.pagination?.total_pages || 0);
    } catch (err) {
      console.error('Error fetching candidates:', err);
    } finally {
      setLoading(false);
    }
  };

  const addCandidate = async (formData) => {
    return await api.post('add-candidate/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Token ${localStorage.getItem('adminToken')}`,
      },
    });
  };

  const deleteCandidate = async (id) => {
    return await api.delete(`admin/delete-candidate/${id}/`, {
      headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` },
    });
  };

  const unarchiveCandidate = async (id) => {
    return await api.post(`admin/unarchive-candidate/${id}/`, {}, {
      headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` },
    });
  };

  const generateInvite = async (candidateId, jobDescriptionId, languageCode = null, scheduleConfig = null) => {
    const payload = {
      candidateId,
      jobDescriptionId
    };
    if (languageCode) {
      payload.language = languageCode;
    }
    if (scheduleConfig) {
      payload.max_no_of_questions = scheduleConfig.max_no_of_questions;
      payload.interview_duration_minutes = scheduleConfig.interview_duration_minutes;
      if (typeof scheduleConfig.require_audio_verification === 'boolean') {
        payload.require_audio_verification = scheduleConfig.require_audio_verification;
      }
    }
    return await api.post('admin/generate-invite/', payload, {
      headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` },
    });
  };

  return {
    candidates,
    setCandidates,
    loading,
    totalCandidates,
    totalPages,
    fetchCandidates,
    addCandidate,
    deleteCandidate,
    unarchiveCandidate,
    generateInvite
  };
};

// Custom hook for managing job descriptions
export const useJobDescriptions = () => {
  const [jobDescriptions, setJobDescriptions] = useState([]);
  const [paginationData, setPaginationData] = useState({
    current_page: 1,
    page_size: 500,
    total_count: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    next_page: null,
    previous_page: null,
  });
  const [loading, setLoading] = useState(false);
  const currentRequestRef = useRef(null);

  const fetchJobDescriptions = useCallback(async (
    page = 1,
    pageSize = 500,
    searchQuery = ''
  ) => {
    setLoading(true);

    // Cancel previous request if exists
    if (currentRequestRef.current) {
      currentRequestRef.current.abort();
    }

    const controller = new AbortController();
    currentRequestRef.current = controller;

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      const res = await api.get(
        `job-descriptions/list_with_pagination/?${params.toString()}`, // <- Add list_with_pagination
        {
          headers: {
            'Authorization': `Token ${localStorage.getItem('adminToken')}`
          },
          signal: controller.signal,
        }
      );

      const { results, pagination } = res.data;

      setJobDescriptions(Array.isArray(results) ? results : []);

      // Update pagination data
      setPaginationData({
        current_page: pagination?.current_page || page,
        page_size: pagination?.page_size || pageSize,
        total_count: pagination?.total_count || 0,
        total_pages: pagination?.total_pages || 1,
        has_next: pagination?.has_next || false,
        has_previous: pagination?.has_previous || false,
        next_page: pagination?.next_page || null,
        previous_page: pagination?.previous_page || null,
      });

      // Save current page & pageSize to localStorage
      localStorage.setItem("jobDescPage", pagination?.current_page || page);
      localStorage.setItem("jobDescPageSize", pagination?.page_size || pageSize);

    } catch (err) {
      // Ignore canceled/aborted requests - these are expected when requests are cancelled
      if (err.name === "AbortError" ||
        err.name === "CanceledError" ||
        err.code === "ERR_CANCELED" ||
        (err.message && err.message.toLowerCase().includes('canceled'))) {
        return;
      }
      console.error('Error fetching job descriptions:', err);
      setJobDescriptions([]);

      // Maintain pagination state even on error
      setPaginationData((prev) => ({
        ...prev,
        current_page: page,
        page_size: pageSize,
        total_count: 0,
        total_pages: 1,
        has_next: false,
        has_previous: false,
        next_page: null,
        previous_page: null,
      }));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobDescriptions();
  }, [fetchJobDescriptions]);

  return {
    jobDescriptions,
    paginationData,
    fetchJobDescriptions,
    loading
  };
};

// Custom hook for skill analysis
export const useSkillAnalysis = () => {
  const [skillAnalysis, setSkillAnalysis] = useState(null);
  const [analyzingSkills, setAnalyzingSkills] = useState(false);

  const analyzeSkills = async (candidateId, jobDescriptionId) => {
    setAnalyzingSkills(true);
    try {
      const analysis = await api.post('admin/analyze-skills/', {
        candidate_id: candidateId,
        job_description_id: jobDescriptionId
      }, {
        headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` },
      });
      setSkillAnalysis(analysis.data);
      return analysis.data;
    } catch (error) {
      console.error('Error analyzing skills:', error);
      throw error;
    } finally {
      setAnalyzingSkills(false);
    }
  };

  const reanalyzeSkills = useCallback(async (candidateId, jobDescriptionId, source = 'both') => {
    setAnalyzingSkills(true);
    try {
      const analysis = await api.post('admin/reanalyze-skills/', {
        candidate_id: candidateId,
        job_description_id: jobDescriptionId,
        source: source
      }, {
        headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` },
      });
      setSkillAnalysis(analysis.data);
      return analysis.data;
    } catch (error) {
      console.error('Error re-analyzing skills:', error);
      throw error;
    } finally {
      setAnalyzingSkills(false);
    }
  }, []); // Remove dependencies since setState functions are stable

  const createInviteWithSkills = async (candidateId, jobDescriptionId, skills, languageCode = null, scheduleConfig = null) => {
    const payload = {
      candidate_id: candidateId,
      job_description_id: jobDescriptionId,
      selectedSkills: skills
    };
    // Always include language - use provided code or default to 'en'
    payload.language = languageCode || 'en';
    if (scheduleConfig) {
      payload.max_no_of_questions = scheduleConfig.max_no_of_questions;
      payload.interview_duration_minutes = scheduleConfig.interview_duration_minutes;
      if (typeof scheduleConfig.require_audio_verification === 'boolean') {
        payload.require_audio_verification = scheduleConfig.require_audio_verification;
      }
    }
    console.log('=== API Hook Debug ===');
    console.log('languageCode parameter:', languageCode);
    console.log('Final payload.language:', payload.language);
    console.log('Full payload:', payload);
    console.log('======================');
    return await api.post('admin/create-invite-with-skills/', payload, {
      headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` },
    });
  };

  const returnObject = {
    skillAnalysis,
    setSkillAnalysis,
    analyzingSkills,
    analyzeSkills,
    reanalyzeSkills,
    createInviteWithSkills
  };

  return returnObject;
};

// Additional hook for feedback and email functionality
export const useFeedback = () => {
  const [feedback, setFeedback] = useState(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [sessionData, setSessionData] = useState(null);

  const fetchFeedback = async (sessionId) => {
    setLoadingFeedback(true);
    setFeedbackError('');
    setFeedback(null);

    try {
      const sessionRes = await api.get(`sessions/${sessionId}/`, {
        headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` },
      });
      setSessionData(sessionRes.data);

      const res = await api.get(`feedback/?session=${sessionId}`, {
        headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` },
      });

      if (!res.data || !res.data.results || res.data.results.length === 0) {
        setFeedbackError('No feedback found for this session.');
        return;
      }
      setFeedback(res.data.results[0]);
    } catch (err) {
      console.error('Error loading feedback:', err);
      setFeedbackError('Failed to load feedback.');
    } finally {
      setLoadingFeedback(false);
    }
  };

  const sendFeedbackEmail = async (emailData) => {
    return await api.post('admin/send-feedback-email/', emailData, {
      headers: { 'Authorization': `Token ${localStorage.getItem('adminToken')}` },
    });
  };

  return {
    feedback,
    setFeedback,
    loadingFeedback,
    feedbackError,
    sessionData,
    setSessionData,
    fetchFeedback,
    sendFeedbackEmail
  };
};